import { type RefObject, useEffect } from 'react';
import { SHORT_LANDSCAPE } from '~portal/styles/breakpoints';

/**
 * @desc Set on the scroll viewport while the reader is moving down the thread; the stylesheets
 * translate the lifted top chrome away for as long as it is there (chat/route.css.ts).
 */
export const HIDE_CHROME_ATTRIBUTE = 'data-hide-chrome';

/** Accumulated travel in one direction before it counts as intent rather than a finger's wobble. */
const THRESHOLD_PX = 10;

/** Within this distance of the top of the thread the chrome is always shown. */
const NEAR_TOP_PX = 48;

/** A reader's own input on the transcript. Scrolls before the first one are not theirs. */
const GESTURES = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * @desc Hide-on-scroll-down, show-on-scroll-up for the top chrome in a short landscape viewport
 * (H2-234).
 *
 * A phone on its side has roughly 330px of page, so the top nav and the chat sub-header lie over
 * the transcript as compact rows. This decides when they get out of the way: down past a small
 * threshold hides them, any deliberate move back up returns them, and near the top of the thread
 * they always show. The rows start visible on every conversation, even though the transcript
 * opens stuck to the bottom: scrolls before the reader's first gesture on the transcript are the
 * app's own (useChatScroll's arrival jump), so they move the baseline but never hide anything.
 *
 * It replaced a CSS scroll-driven animation that tied the rows 1:1 to the first 96px of the
 * thread. That was listener-free, but a thread opens at its end — thousands of pixels from the
 * top — so the rows were hidden on arrival and came back only at the very top.
 *
 * Cost per scroll event: at most one `scrollTop` read per animation frame and no React state;
 * the only write is the attribute, and only when the outcome actually flips. Everything else —
 * the translation, its transition, reduced motion — is the stylesheet's. Outside the media query
 * no listener is attached at all, and turning the phone upright lets go of the attribute.
 */
export function useHideOnScroll(
  scrollRef: RefObject<HTMLElement | null>,
  resetKey: string,
  query: string = SHORT_LANDSCAPE,
) {
  useEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport) return;

    const media = window.matchMedia(query);
    let detach: (() => void) | undefined;

    function attach(el: HTMLElement) {
      let hidden = false;
      let armed = false;
      let lastTop = el.scrollTop;
      let travel = 0;
      let frame = 0;

      const apply = (next: boolean) => {
        if (next === hidden) return;
        hidden = next;
        el.toggleAttribute(HIDE_CHROME_ATTRIBUTE, next);
      };

      const read = () => {
        frame = 0;
        const top = el.scrollTop;
        const delta = top - lastTop;
        lastTop = top;
        if (top <= NEAR_TOP_PX) {
          travel = 0;
          apply(false);
          return;
        }
        if (!armed || delta === 0) return;
        // Travel accumulates while the direction holds and restarts when it turns, so a reversal
        // has to clear the threshold on its own before it counts.
        travel = Math.sign(delta) === Math.sign(travel) ? travel + delta : delta;
        if (Math.abs(travel) >= THRESHOLD_PX) apply(travel > 0);
      };

      const onScroll = () => {
        if (!frame) frame = requestAnimationFrame(read);
      };
      const arm = () => {
        armed = true;
      };

      el.addEventListener('scroll', onScroll, { passive: true });
      GESTURES.forEach((type) => el.addEventListener(type, arm, { passive: true }));

      return () => {
        el.removeEventListener('scroll', onScroll);
        GESTURES.forEach((type) => el.removeEventListener(type, arm));
        if (frame) cancelAnimationFrame(frame);
        el.removeAttribute(HIDE_CHROME_ATTRIBUTE);
      };
    }

    // Rotation crosses the query both ways, so listen for it rather than deciding once.
    const sync = () => {
      detach?.();
      detach = media.matches ? attach(viewport) : undefined;
    };

    sync();
    media.addEventListener('change', sync);
    return () => {
      media.removeEventListener('change', sync);
      detach?.();
    };
  }, [scrollRef, resetKey, query]);
}
